/*!
 * Internal-search adapter for AMOUR Parfums theme.
 *
 * Acts as the PRIMARY search engine for the predictive-search dropdown,
 * overriding Shopify's default product search ranking which struggles with
 * our highly-homogenous catalogue (every product titled "AMOUR No. <N>",
 * with near-identical body_html). Shopify's TF-IDF gives "AMOUR" / "No"
 * zero IDF weight and treats "1" as a substring of "10", "100", "173",
 * etc., so e.g. searching "AMOUR No. 1" never returns AMOUR No. 1.
 *
 * MiniSearch indexes a JSON dump of all Parfumi products from the smart
 * collection 'int-idx-xyz' (one entry per product, exposed via
 * templates/collection.search-index.liquid). product_no and title get a
 * heavy boost so exact-N queries (e.g. "1", "AMOUR No. 1", "no.1") return
 * the precise product first.
 *
 * Replacement strategy:
 *   - On user input we always run MiniSearch first and render results into
 *     the predictive dropdown's product list.
 *   - When Shopify's predictive AJAX response lands afterwards, a
 *     MutationObserver detects the injection and overwrites the product
 *     list with our MiniSearch results again (other Shopify result types
 *     like collections / pages / articles are kept as-is).
 *   - When MiniSearch finds zero hits we render an explicit empty state.
 *
 * Smart query handler:
 *   - Patterns "amour no X", "AMOUR No. X", "amour X", "no X", "no.X",
 *     or a bare number "X" are normalized to product_no=X and matched
 *     against the indexed product_no field. This guarantees deterministic
 *     placement of the exact product on top.
 *
 * The script lazy-loads on first user interaction with the search input.
 */
(function () {
  'use strict';

  var INTERNAL_COLLECTION_HANDLE = 'int-idx-xyz';
  var INDEX_URL = '/collections/' + INTERNAL_COLLECTION_HANDLE + '?view=search-index';
  var SEARCH_INPUT_SELECTOR = '.search__input';
  var DROPDOWN_SELECTOR = '#predictive-search';
  var MIN_QUERY_LENGTH = 1; // single-digit product_no must work ("1", "5")
  var MAX_RESULTS = 12;
  var DEBOUNCE_MS = 180;
  var INTERNAL_FLAG = 'data-internal-search-id';
  // localStorage cache. Bumping the version key invalidates all clients
  // (e.g. when the index template format changes). TTL guarantees that
  // newly published / edited products show up within a working day even
  // if the user never clears their cache.
  // v3 (2026-05-28): runWithConcurrency had a `var idx` in async-loop
  // bug that silently dropped most pages of the search index (only ~200
  // out of ~520 products were ever cached). All v1/v2 caches must be
  // invalidated to rebuild a complete index.
  // v4 (2026-06-29): index now carries scent_family + notes, and we derive
  // an `accords` field client-side (see ACCORD_KEYWORDS). Needed so the
  // search-discovery chips (SPOL / DIŠAVNE DRUŽINE / VONJNI AKORDI) return
  // matching products. Bumping invalidates all v1–v3 caches.
  var CACHE_KEY = 'amour_search_index_v4';
  var CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
  var MAX_PAGES = 25;          // safety cap (1250 products)
  var FETCH_CONCURRENCY = 6;   // matches typical browser per-host connection limit
  // Patterns that indicate the user is searching for AMOUR No. <N>.
  // Captured group 1 is the product_no value (digits with optional
  // single trailing letter, e.g. "10", "10n", "417p"). The leading
  // "amour"/"no" pieces are all optional so a bare number works too.
  var AMOUR_NO_RE = /^\s*(?:amour\s*)?(?:no\.?\s*|n\.?\s*|nr\.?\s*|szam\s*|sz\.?\s*|broj\s*|br\.?\s*|st\.?\s*)?(\d+[a-z]?)\s*$/i;

  var indexPromise = null;
  var miniSearch = null;
  var products = [];
  var productsByProductNo = null;
  var lastQuery = '';
  // When the active search was launched from a discovery chip that maps to a
  // real collection (e.g. the gender chips → /collections/zenski-parfumi), the
  // "view all" CTA should land on that curated collection instead of a fuzzy
  // /search query. Cleared whenever the shopper types their own query.
  var chipViewAllHref = '';
  var observers = [];

  // ── Index loading ─────────────────────────────────────────────
  // The Parfumi catalogue is ~450–520 products. Shopify caps the
  // server-side `paginate` tag at 50 items per page, which means we
  // need 9–11 HTTP round trips to assemble the full search index.
  //
  // Performance tactics applied here:
  //   1. PARALLEL fetch of pages 2..N once page 1 reveals
  //      `total_pages` (browsers serialize 6 connections per host
  //      anyway, but parallel still beats sequential ~7×).
  //   2. localStorage cache with a short TTL so subsequent page
  //      loads — including the very common "search a perfume,
  //      bounce to a category, search again" flow — are instant.
  //   3. Stale-while-revalidate: when the cache is hit we render
  //      immediately AND kick off a background refetch so any
  //      newly published product is picked up without forcing the
  //      user to wait. Only the next loadIndex() call sees the
  //      refreshed data — current session keeps the snapshot it
  //      started with to avoid mid-keystroke result reshuffles.
  //
  // The `page` parameter is always included (even for page 1) so the
  // URL never collides with an older cache entry of the legacy
  // single-array template format that Cloudflare may have retained.
  function fetchPage(page) {
    var url = INDEX_URL + (INDEX_URL.indexOf('?') === -1 ? '?' : '&') + 'page=' + page;
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('Index HTTP ' + r.status + ' (page ' + page + ')');
      return r.text();
    });
  }

  function parsePage(text, pageNo) {
    var data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      return { page: pageNo, has_next: false, total_pages: 1, products: [] };
    }
    // Backwards compat: legacy template revisions emitted a bare array.
    if (Array.isArray(data)) {
      return { page: pageNo, has_next: false, total_pages: 1, products: data };
    }
    return data;
  }

  // Run a list of factory functions returning Promises with a max
  // concurrency. Standard pattern; avoids spinning up 25 in-flight
  // fetches at once (browsers block past 6/host anyway, but this also
  // keeps the request stream tidy).
  function runWithConcurrency(taskFactories, concurrency) {
    return new Promise(function (resolve, reject) {
      var results = new Array(taskFactories.length);
      var nextIdx = 0;
      var activeCount = 0;
      var completed = 0;
      var failed = false;

      function pump() {
        if (failed) return;
        while (activeCount < concurrency && nextIdx < taskFactories.length) {
          // CRITICAL: wrap in IIFE so each async callback captures its
          // own `idx`. Without this, `var idx` is function-scoped to
          // pump() and every in-flight callback ends up referencing
          // the last assigned value — only one slot per pump cycle
          // gets populated and most pages are silently dropped from
          // the assembled index. (Manifests as "Stronger" returning 1
          // hit instead of 2 because pages 5–10 are lost.)
          (function (idx) {
            activeCount++;
            taskFactories[idx]().then(function (val) {
              results[idx] = val;
              activeCount--;
              completed++;
              if (completed === taskFactories.length) resolve(results);
              else pump();
            }, function (err) {
              failed = true;
              reject(err);
            });
          })(nextIdx++);
        }
      }
      if (!taskFactories.length) resolve(results);
      else pump();
    });
  }

  function fetchAllPages() {
    return fetchPage(1).then(function (text) {
      var first = parsePage(text, 1);
      var collected = (first.products || []).slice();
      var totalPages = first.total_pages || 1;
      if (!first.has_next || totalPages <= 1) return collected;

      var lastPage = Math.min(totalPages, MAX_PAGES);
      var tasks = [];
      for (var p = 2; p <= lastPage; p++) {
        (function (pageNo) {
          tasks.push(function () {
            return fetchPage(pageNo).then(function (t) { return parsePage(t, pageNo); });
          });
        })(p);
      }
      return runWithConcurrency(tasks, FETCH_CONCURRENCY).then(function (pages) {
        for (var i = 0; i < pages.length; i++) {
          if (pages[i] && pages[i].products && pages[i].products.length) {
            collected = collected.concat(pages[i].products);
          }
        }
        return collected;
      });
    });
  }

  function readCache() {
    if (typeof localStorage === 'undefined') return null;
    try {
      var raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.t || !Array.isArray(parsed.products)) return null;
      if (Date.now() - parsed.t > CACHE_TTL_MS) {
        try { localStorage.removeItem(CACHE_KEY); } catch (e) {}
        return null;
      }
      return parsed.products;
    } catch (e) {
      return null;
    }
  }

  function writeCache(products) {
    if (typeof localStorage === 'undefined') return;
    if (!products || !products.length) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), products: products }));
    } catch (e) {
      // Quota / private mode — just skip. Search still works, just slower next time.
      try { localStorage.removeItem(CACHE_KEY); } catch (e2) {}
    }
  }

  // Brand-name search toggle (theme setting "Allow searching by brand name").
  // When disabled, buildSearchDocs scrubs the inspired-by brand out of the
  // INDEXED text so a brand query matches nothing, while the display objects
  // keep the real fragrance name. We scrub at build time (not at cache time)
  // so the cached index stays complete and toggling the setting takes effect
  // on the next page load without a cache rebuild.
  function brandSearchEnabled() {
    try {
      if (window.theme && theme.settings && typeof theme.settings.searchBrandTags !== 'undefined') {
        return theme.settings.searchBrandTags !== false;
      }
    } catch (e) {}
    return true;
  }

  // Build the documents that MiniSearch INDEXES. When brand search is off we
  // scrub every trace of the inspired-by brand from the searchable text so a
  // brand query ("kenzo") matches nothing — even though the brand also appears
  // inside the fragrance name ("KENZO HOMME"), the inspiration metafield
  // ("KENZO FLOWERS") and product-fragrance tags ("mf_pf-kenzo-homme").
  // The DISPLAY objects (the `products` array used by renderPredictiveCard)
  // are left untouched, so result labels still show the real fragrance name.
  // Maps a visible "scent accord" (VONJNI AKORDI chip label) to the
  // Slovenian note stems that imply it. Used to derive a per-product
  // `accords` field from the raw notes text so e.g. the "Citrusni" chip
  // returns products whose notes contain bergamotka / limona / mandarina …
  // (the literal word "citrusni" never appears in the notes themselves).
  var ACCORD_KEYWORDS = {
    'citrusni': ['bergamot', 'limon', 'limet', 'grenivk', 'mandarin', 'pomaranč', 'neroli', 'citrus', 'bigarad', 'yuzu', 'klementin', 'verben'],
    'lesni': ['cedr', 'sandalov', 'pačul', 'paciuli', 'vetiver', 'palisand', 'lesn', ' les ', 'gvajak', 'bor ', 'smrek', 'udov les', 'oud'],
    'cvetlični': ['vrtnic', 'jasmin', 'tubero', 'ilang', 'ylang', 'šmarnic', 'mimoz', 'frezij', 'gardenij', 'vijolic', 'magnolij', 'peoni', 'lotos', ' cvet', 'lila', 'orhidej', 'sambac'],
    'sadni': ['sliv', 'marelic', 'jabolk', 'ribez', 'breskev', 'hrušk', 'ananas', 'kokos', 'jagod', 'malin', 'mango', 'robid', 'fig', 'grozdj', 'melon', 'papaj', 'češnj', 'lubenic', 'litči', 'nektarin'],
    'sladki': ['pralin', 'kakav', 'čokolad', 'tonka', 'karamel', ' med ', 'sladkor', 'marshmallow', 'sladk', 'nugat', 'sladoled', 'praline'],
    'vaniljev': ['vanilij'],
    'jantarni': ['ambra', 'jantar', 'labdanum', 'benzoj', 'smol'],
    'mošusni': ['mošus'],
    'vrtnični': ['vrtnic'],
    'usnjeni': ['usnj', 'koža'],
    'sveži': ['svež', 'morsk', 'vod', 'akvatičn', ' meta', 'zelen', 'list', 'ozon', 'mint', 'bambus', 'kumaric'],
    'oud': ['oud', 'agarov', 'agar ', 'udov les', 'oudov']
  };

  function computeAccords(notes, family) {
    var hay = (' ' + String(notes == null ? '' : notes) + ' ' +
               String(family == null ? '' : family) + ' ')
      .toLowerCase().replace(/,/g, ' ');
    var out = [];
    for (var label in ACCORD_KEYWORDS) {
      if (!Object.prototype.hasOwnProperty.call(ACCORD_KEYWORDS, label)) continue;
      var kws = ACCORD_KEYWORDS[label];
      for (var i = 0; i < kws.length; i++) {
        if (hay.indexOf(kws[i]) !== -1) { out.push(label); break; }
      }
    }
    return out.join(' ');
  }

  function buildSearchDocs(allProducts) {
    if (brandSearchEnabled()) return allProducts;
    return allProducts.map(function (p) {
      if (!p) return p;
      var brand = String(p.dek_vendor == null ? '' : p.dek_vendor).toLowerCase().trim();
      function scrub(s) {
        s = String(s == null ? '' : s).toLowerCase();
        if (brand) s = s.split(brand).join(' ');
        return s;
      }
      var rawTags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
      var tags = rawTags
        .filter(function (t) { return String(t).toLowerCase().indexOf('mf_dv') === -1; })
        .map(function (t) { return scrub(t); });
      return {
        id: p.id,
        title: p.title,            // "AMOUR No. X" — never carries the brand
        vendor: scrub(p.vendor),
        type: p.type,
        tags: tags,
        fragrance: scrub(p.fragrance),
        inspiration: '',           // = "<BRAND> <fragrance>" → drop entirely
        dek_vendor: '',            // the brand itself → drop
        product_no: p.product_no,
        handle: scrub(p.handle),
        gender: p.gender,
        // Discovery-chip fields — carried through untouched (no brand to scrub).
        scent_family: p.scent_family,
        notes: p.notes,
        accords: p.accords,
        description: scrub(p.description)
      };
    });
  }

  function buildMiniSearch(allProducts) {
    // Keep the untouched originals for display/lookup …
    products = allProducts;
    // Derive the `accords` field from notes (+ family) up-front so it is
    // available on both the brand-on (raw) and brand-off (scrubbed) doc paths.
    allProducts.forEach(function (p) {
      if (p) p.accords = computeAccords(p.notes, p.scent_family);
    });
    // … and index a (possibly brand-scrubbed) copy.
    var docs = buildSearchDocs(allProducts);
    // Build a separate hash for direct product_no lookup so we can
    // bypass MiniSearch entirely when the user types a precise
    // AMOUR No. N pattern. Lower-cased to handle "10n", "417P" etc.
    productsByProductNo = Object.create(null);
    products.forEach(function (p) {
      if (p && p.product_no != null) {
        productsByProductNo[String(p.product_no).toLowerCase()] = p;
      }
    });
    miniSearch = new MiniSearch({
      idField: 'id',
      // Searched fields. Boosts below intentionally make product_no
      // dominate — it is the most distinguishing token in our
      // catalogue (everything else is near-identical). The brand-off
      // scrubbing happens in buildSearchDocs, not here.
      fields: [
        'title', 'vendor', 'type', 'tags', 'fragrance', 'inspiration',
        'dek_vendor', 'product_no', 'handle', 'gender', 'description',
        'scent_family', 'notes', 'accords'
      ],
      storeFields: ['id'],
      searchOptions: {
        boost: {
          product_no: 12,
          title: 5,
          handle: 4,
          fragrance: 3,
          inspiration: 3,
          dek_vendor: 2,
          vendor: 1.5
        },
        prefix: true,
        fuzzy: 0.2,
        combineWith: 'AND'
      },
      extractField: function (doc, field) {
        var v = doc[field];
        if (v == null) return '';
        if (Array.isArray(v)) return v.join(' ');
        return String(v);
      }
    });
    miniSearch.addAll(docs);
  }

  function loadIndex() {
    if (indexPromise) return indexPromise;
    if (typeof MiniSearch === 'undefined') {
      return Promise.reject(new Error('MiniSearch not loaded'));
    }

    var cached = readCache();
    if (cached && cached.length) {
      // Cache hit: surface immediately so the very first keystroke
      // already has results to render. Kick off a background refetch
      // (fire-and-forget) to refresh the cache for the next visit;
      // we deliberately do NOT swap the live MiniSearch instance
      // mid-session, because that would re-rank results between
      // keystrokes and feel jittery.
      buildMiniSearch(cached);
      indexPromise = Promise.resolve(cached);
      fetchAllPages().then(function (fresh) {
        if (fresh && fresh.length) writeCache(fresh);
      }).catch(function () { /* ignore — we already have data */ });
      return indexPromise;
    }

    indexPromise = fetchAllPages()
      .then(function (allProducts) {
        buildMiniSearch(allProducts);
        writeCache(allProducts);
        return allProducts;
      })
      .catch(function (err) {
        if (window.console) console.warn('[internal-search] index load failed', err);
        indexPromise = null;
        throw err;
      });
    return indexPromise;
  }

  // Try to interpret the query as an AMOUR No. lookup and return the
  // exact product_no match (if any). Returns null when the query does
  // not look like a product-number lookup or the number is unknown.
  function exactProductNoMatch(query) {
    if (!query || !productsByProductNo) return null;
    var m = AMOUR_NO_RE.exec(query.trim());
    if (!m) return null;
    var key = m[1].toLowerCase();
    return productsByProductNo[key] || null;
  }

  function search(query) {
    if (!miniSearch || !query) return [];
    var trimmed = query.trim();
    var exact = exactProductNoMatch(trimmed);
    var hits = miniSearch.search(trimmed);
    var byId = Object.create(null);
    products.forEach(function (p) { byId[p.id] = p; });
    var results = hits
      .map(function (h) { return byId[h.id]; })
      .filter(Boolean);
    // If an exact product_no lookup matched, hoist that product to the
    // top (and dedupe in case MiniSearch already returned it). This is
    // the deterministic guarantee that "AMOUR No. 1" → AMOUR No. 1.
    if (exact) {
      results = [exact].concat(results.filter(function (p) { return p && p.id !== exact.id; }));
    }
    return results;
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  var PERFUME_TYPES = ['Perfumes', 'Parfumi', 'Parfümök', 'Parfemi', 'Parfüme', 'Parfums'];

  function getPerfumeLabel() {
    return (window.theme && theme.strings && theme.strings.perfumeLabel) ||
           'Parfum, navdihnjen po:';
  }

  // Format a price (in cents) using the SAME money format as the rest of the
  // store, so the dropdown price matches the cart / product pages exactly.
  function formatMoney(cents) {
    try {
      if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
        return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      }
    } catch (e) {}
    return (Number(cents || 0) / 100).toFixed(2);
  }

  // "from" prefix for price-varies products, localised by <html lang>.
  function getFromLabel() {
    var lang = (document.documentElement.getAttribute('lang') || '').slice(0, 2).toLowerCase();
    var map = { sl: 'od', si: 'od', en: 'from', de: 'ab', it: 'da', hr: 'od', hu: '' };
    return map.hasOwnProperty(lang) ? map[lang] : 'od';
  }

  function renderPriceHtml(p) {
    if (typeof p.price !== 'number') return '';
    var varies = p.price_varies === true && typeof p.price_min === 'number';
    var amount = varies ? p.price_min : p.price;
    var prefix = varies ? (getFromLabel() ? getFromLabel() + ' ' : '') : '';
    return '<span class="grid-product__price amour-search-price">' +
      escapeHtml(prefix) + formatMoney(amount) + '</span>';
  }

  function renderPredictiveCard(p) {
    var labelHtml;
    if (PERFUME_TYPES.indexOf(p.type) !== -1) {
      // AMOUR Parfums originals (where deklaracije_vendor is the
      // in-house brand, not a luxury reference) are NOT inspired by
      // anything external — render the product title directly so the
      // dropdown shows e.g. "AMOUR No. 1" instead of the misleading
      // "Parfum, navdihnjen po: AMOUR Parfums - AMOUR No. 1".
      // Other Parfumi products keep the full inspired-by label plus
      // the gender / product_no secondary line.
      var dekUpper = (p.dek_vendor || '').toUpperCase();
      if (dekUpper.indexOf('AMOUR PARFUMS') !== -1) {
        labelHtml = '<span>' + escapeHtml(p.title) + '</span>';
      } else {
        // Mirror the cart drawer: product title ("AMOUR No. X") as the
        // primary line, and the inspired-by subtitle ("<perfume label>
        // <FRAGRANCE>") below it. The inspired-by brand is intentionally
        // never shown — only the fragrance name, exactly like the cart
        // (templates/product.cartdata.liquid ships dek="" on purpose).
        // Must match the Liquid version in snippets/search-results.liquid.
        var frag = (p.fragrance || '').trim();
        var subParts = [];
        var perfumeLabel = getPerfumeLabel();
        if (perfumeLabel) subParts.push(escapeHtml(perfumeLabel));
        if (frag) subParts.push(escapeHtml(frag));
        labelHtml =
          '<span>' + escapeHtml(p.title) + '</span>' +
          (frag
            ? '<span class="gender-field amour-search-inspired">' +
              subParts.join(' ') +
              '</span>'
            : '');
      }
    } else {
      labelHtml = '<span>' + escapeHtml(p.title) + '</span>';
    }

    var imageHtml = p.image
      ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="80" height="80">'
      : '';

    var inspiredHtml = '';
    // Skip the inspired-by image for AMOUR Parfums originals — they
    // are not inspired by anything, so the secondary "fragrance bottle"
    // overlay would be misleading. The `p.inspired_by` flag is set
    // server-side from the metafield, but defending here too keeps
    // the dropdown safe even if the metafield is incorrectly populated.
    var dekUpperImg = (p.dek_vendor || '').toUpperCase();
    var isAmourOriginal = dekUpperImg.indexOf('AMOUR PARFUMS') !== -1;
    if (p.inspired_by && !isAmourOriginal) {
      var shopDomain = (window.theme && theme.shop && theme.shop.permanentDomain) || '';
      var fallback = p.image || '';
      var smallSrc = p.image_small || p.image || '';
      inspiredHtml =
        '<div class="product-inspired-by search-result-inspired">' +
          '<img ' +
            'src="' + escapeHtml(smallSrc) + '" ' +
            'loading="lazy" ' +
            'width="300" ' +
            'height="300" ' +
            'data-inspired-product-id="' + escapeHtml(String(p.id)) + '" ' +
            'data-inspired-shop="' + escapeHtml(shopDomain) + '" ' +
            'data-inspired-width="300" ' +
            'data-inspired-fallback="' + escapeHtml(fallback) + '"' +
          '>' +
        '</div>';
    }

    return (
      '<li role="option" ' + INTERNAL_FLAG + '="' + p.id + '">' +
        '<a href="' + escapeHtml(p.url) + '">' +
          '<div class="results-products__image grid__image-ratio ffff">' +
            imageHtml +
            inspiredHtml +
          '</div>' +
          '<div class="results-products__info gggg">' +
            labelHtml +
            renderPriceHtml(p) +
          '</div>' +
        '</a>' +
      '</li>'
    );
  }

  function getDropdowns() {
    return Array.prototype.slice.call(document.querySelectorAll(DROPDOWN_SELECTOR));
  }

  function clearInjections(dropdown) {
    // Remove our standalone <li> inserts (legacy append mode) and any
    // empty section we created earlier. Note: when the product <ul> has
    // INTERNAL_FLAG on it (primary-engine replace mode) we leave the
    // <ul> in place — the next injectInto() call will overwrite its
    // innerHTML with the new query's results.
    var nodes = dropdown.querySelectorAll('li[' + INTERNAL_FLAG + ']');
    Array.prototype.forEach.call(nodes, function (n) { n.remove(); });
    var emptySection = dropdown.querySelector('.results--products[data-internal-search-section]');
    if (emptySection) emptySection.remove();
    var ourBtn = dropdown.querySelector('.results__search-btn[data-internal-search-section]');
    if (ourBtn) ourBtn.remove();
  }

  function viewAllLabel() {
    return (window.theme && theme.strings && theme.strings.searchViewMore) || 'Prikaži vse rezultate';
  }

  // Immediate loading feedback: while the index loads / the query resolves,
  // show shimmering skeleton rows so the dropdown never looks empty (which
  // shoppers misread as "no results"). Only shown when there are no real
  // results yet, so fast typing doesn't flash the skeleton on every keystroke.
  function skeletonRowsHtml() {
    var row = '<li class="results-products__item amour-skel-row" aria-hidden="true">' +
      '<span class="amour-skel amour-skel--img"></span>' +
      '<span class="amour-skel-lines">' +
        '<span class="amour-skel amour-skel--l1"></span>' +
        '<span class="amour-skel amour-skel--l2"></span>' +
        '<span class="amour-skel amour-skel--l3"></span>' +
      '</span>' +
    '</li>';
    return row + row + row + row;
  }

  function showLoadingIfEmpty(dropdown) {
    if (!dropdown) return;
    // A real (non-skeleton) product row already on screen → keep it visible
    // until the new results replace it, rather than flashing the skeleton.
    if (dropdown.querySelector('.results--products li:not(.amour-skel-row)')) return;
    clearInjections(dropdown);
    var heading = (window.theme && theme.strings && theme.strings.searchProducts) || 'Izdelki';
    var section = document.createElement('div');
    section.className = 'results results--products';
    section.setAttribute('data-internal-search-section', '1');
    section.setAttribute('data-amour-loading', '1');
    section.innerHTML =
      '<h3 class="h4">' + escapeHtml(heading) + '</h3>' +
      '<ul role="listbox">' + skeletonRowsHtml() + '</ul>';
    var root = dropdown.querySelector('.results__group-2') ||
               dropdown.querySelector('.predictive-search-results') || dropdown;
    root.appendChild(section);
    dropdown.style.display = 'block';
  }

  function injectInto(dropdown, query) {
    if (!dropdown) return;
    clearInjections(dropdown);

    var matches = search(query).slice(0, MAX_RESULTS);
    var html = matches.map(renderPredictiveCard).join('');
    var existingList = dropdown.querySelector('.results--products ul');

    // Primary-engine mode: REPLACE Shopify's product list with our own.
    // Shopify's default ranker gives non-deterministic results for our
    // homogenous catalogue, so we always overwrite. Other result types
    // in the dropdown (collections / pages / articles) remain untouched.
    if (existingList) {
      if (matches.length) {
        existingList.innerHTML = html;
        existingList.setAttribute(INTERNAL_FLAG, '1');
      } else {
        // No MiniSearch hits — clear product results entirely so the
        // user doesn't see Shopify's misleading near-matches.
        existingList.innerHTML = '';
        existingList.setAttribute(INTERNAL_FLAG, '1');
      }
      return;
    }

    if (!matches.length) return;

    var resultsRoot =
      dropdown.querySelector('.results__group-2') ||
      dropdown.querySelector('.predictive-search-results') ||
      dropdown;

    if (!resultsRoot) return;

    var noneEl = dropdown.querySelector('.predictive-search-results--none');
    if (noneEl) {
      noneEl.classList.remove('predictive-search-results--none');
    }
    var noResultsLink = dropdown.querySelector('.predictive-search__no-results');
    if (noResultsLink) {
      var li = noResultsLink.closest('.results') || noResultsLink.parentNode;
      if (li) li.remove();
    }

    var section = document.createElement('div');
    section.className = 'results results--products';
    section.setAttribute('data-internal-search-section', '1');
    var heading = (window.theme && theme.strings && theme.strings.searchProducts) || 'Izdelki';
    section.innerHTML =
      '<h3 class="h4">' + escapeHtml(heading) + '</h3>' +
      '<ul role="listbox">' + html + '</ul>';
    resultsRoot.appendChild(section);

    // Native predictive renders the "view all" CTA only when Shopify itself
    // returned products. Our discovery chips (scent family / accords / gender)
    // never hit Shopify's index, so add the same full-width CTA here, linking
    // to the full /search page where injectFullSearchResults() surfaces them.
    if (matches.length && !dropdown.querySelector('.results__search-btn')) {
      var viewAll = document.createElement('a');
      viewAll.className = 'results__search-btn';
      viewAll.setAttribute('data-internal-search-section', '1');
      var viewAllHref = chipViewAllHref ||
        ('/search?q=' + encodeURIComponent(query) + '&type=product&options[prefix]=last');
      viewAll.setAttribute('href', viewAllHref);
      viewAll.innerHTML = escapeHtml(viewAllLabel()) +
        ' <svg viewBox="0 0 14 10" aria-hidden="true" focusable="false"><path d="M1 5h12M9 1l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      dropdown.appendChild(viewAll);
    }
  }

  function injectAll(query) {
    getDropdowns().forEach(function (d) { injectInto(d, query); });
  }

  function attachObserver(dropdown) {
    if (!dropdown || dropdown._internalSearchObserved) return;
    dropdown._internalSearchObserved = true;
    // When Shopify's predictive-search AJAX response lands it injects
    // a fresh products <ul> into the dropdown, wiping our replaced one.
    // We re-run injectInto() to overwrite Shopify's results with ours
    // again. Throttled via requestAnimationFrame to avoid loops.
    var pending = false;
    var ob = new MutationObserver(function (mutations) {
      var hasExternalChange = mutations.some(function (m) {
        return Array.prototype.some.call(m.addedNodes, function (n) {
          if (n.nodeType !== 1) return false;
          // Skip mutations we caused ourselves (innerHTML replace
          // doesn't trigger here, but defense-in-depth).
          if (n.hasAttribute && n.hasAttribute(INTERNAL_FLAG)) return false;
          if (n.getAttribute && n.getAttribute('data-internal-search-section') === '1') return false;
          return true;
        });
      });
      if (hasExternalChange && lastQuery && !pending) {
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          injectInto(dropdown, lastQuery);
        });
      }
    });
    ob.observe(dropdown, { childList: true, subtree: true });
    observers.push(ob);
  }

  function attachObservers() {
    getDropdowns().forEach(attachObserver);
  }

  function handleInputEvent(input) {
    if (input._internalSearchBound) return;
    input._internalSearchBound = true;

    var debounceTimer = null;
    input.addEventListener('input', function () {
      var q = input.value.trim();
      lastQuery = q;
      // The shopper is typing their own query — drop any chip collection target.
      chipViewAllHref = '';
      clearTimeout(debounceTimer);
      // Toggle the discovery overlay: visible while the query is empty,
      // hidden the moment the shopper starts typing (results take over).
      setDiscoveryQueryState(input, q.length >= MIN_QUERY_LENGTH);

      if (q.length < MIN_QUERY_LENGTH) {
        // Emptying the box returns to the discovery chips: wipe any leftover
        // results (native + ours) and collapse the panel so only the
        // discovery overlay (a sibling of #predictive-search) remains.
        getDropdowns().forEach(function (dd) {
          clearInjections(dd);
          dd.innerHTML = '';
          dd.style.display = 'none';
        });
        return;
      }

      // Instant feedback so the gap before results never reads as "no hits".
      getDropdowns().forEach(showLoadingIfEmpty);

      debounceTimer = setTimeout(function () {
        loadIndex()
          .then(function () {
            attachObservers();
            injectAll(q);
          })
          .catch(function () { /* already logged */ });
      }, DEBOUNCE_MS);
    });

    input.addEventListener('focus', function () {
      // warm the index on first focus to remove perceived latency
      if (!indexPromise) loadIndex().catch(function () {});
      // Show the discovery chips immediately on focus when the box is empty.
      setDiscoveryQueryState(input, input.value.trim().length >= MIN_QUERY_LENGTH);
      // Mobile: open the theme's full-screen search overlay the moment the
      // box is tapped. The theme normally only adds `.is-active` (which drives
      // the full-screen styling) on the first keystroke, so a plain tap left
      // the search rendered inline — full-screen "sometimes". Replicating the
      // theme's own open() side-effects here makes it full-screen every time.
      openFullscreenIfMobile(input);
    });
  }

  // ── Search discovery overlay (chips) ─────────────────────────────
  // Renders nothing itself — the markup is server-rendered by
  // snippets/amour-search-discovery.liquid inside each <predictive-search>.
  // Here we only (a) toggle its visibility vs. the results, (b) turn a chip
  // click into an instant search, and (c) open OUR perfume assistant.
  function closestPredictiveSearch(el) {
    return el && typeof el.closest === 'function' ? el.closest('predictive-search') : null;
  }

  function setDiscoveryQueryState(input, hasQuery) {
    var ps = closestPredictiveSearch(input);
    if (ps) ps.classList.toggle('amour-has-query', !!hasQuery);
  }

  function isMobileViewport() {
    return window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
  }

  // Force the native full-screen search on mobile as soon as the field is
  // engaged. Mirrors the side-effects of the theme's `predictive-search:open`
  // handler (assets/theme.js): add `.is-active` + lock body scroll. The theme's
  // own close() (X button / backdrop / Esc) already reverses all of these.
  function openFullscreenIfMobile(input) {
    if (!isMobileViewport()) return;
    var ps = closestPredictiveSearch(input);
    if (!ps || ps.classList.contains('is-active')) return;
    ps.classList.add('is-active');
    document.body.classList.add('predictive-overflow-hidden');
    document.body.style.overflowY = 'hidden';
  }

  function openPerfumeAssistant() {
    // Repointed from Tidio to OUR assistant. Prefer the guided assistant tab
    // (window.AmourAssistant.openAssistant from /widget.js); fall back to a plain
    // open(). Fully guarded so a click never throws even before the widget loads.
    try {
      var A = window.AmourAssistant;
      if (A && typeof A.openAssistant === 'function') { A.openAssistant(); return; }
      if (A && typeof A.open === 'function') { A.open(); return; }
    } catch (e) {}
  }

  function runChipSearch(chip) {
    var ps = closestPredictiveSearch(chip);
    if (!ps) return;
    var query = chip.getAttribute('data-query') || (chip.textContent || '').trim().toLowerCase();
    if (!query) return;
    // The visible label is localised (EN/DE/HR/HU/IT/SL) while `data-query`
    // stays Slovenian — that's the language the product notes / gender / derived
    // accords are indexed in. Show the localised label in the box, but always
    // search with the Slovenian term so results are identical across locales.
    var label = (chip.textContent || '').trim() || query;
    var input = ps.querySelector(SEARCH_INPUT_SELECTOR) || ps.querySelector('input[type="search"]');
    if (input) { input.value = label; input.focus(); }
    lastQuery = query;
    // Remember a curated destination for the "view all" CTA (gender chips).
    chipViewAllHref = chip.getAttribute('data-href') || '';
    ps.classList.add('is-active', 'amour-has-query');
    // We deliberately DON'T dispatch a native `input` event here: the chip
    // terms (scent family / accords / gender) almost never hit Shopify's own
    // predictive endpoint, and its .catch() would call close() and wipe our
    // results. Instead we reveal the panel ourselves — exactly what the
    // theme's open() does — and render the ranked products directly. If the
    // shopper edits the box afterwards, the native typing flow takes over.
    var dropdown = ps.querySelector(DROPDOWN_SELECTOR);
    if (dropdown) { dropdown.style.display = 'block'; showLoadingIfEmpty(dropdown); }
    loadIndex().then(function () {
      attachObservers();
      if (dropdown) injectInto(dropdown, query);
    }).catch(function () {});
  }

  function bindDiscoveryDelegation() {
    if (document._amourDiscoveryBound) return;
    document._amourDiscoveryBound = true;
    document.addEventListener('click', function (e) {
      if (!e.target || typeof e.target.closest !== 'function') return;
      var assistant = e.target.closest('[data-amour-assistant]');
      if (assistant) { e.preventDefault(); openPerfumeAssistant(); return; }
      var chip = e.target.closest('[data-amour-chip]');
      if (chip) { e.preventDefault(); runChipSearch(chip); }
    });
  }

  function bindInputs() {
    Array.prototype.forEach.call(
      document.querySelectorAll(SEARCH_INPUT_SELECTOR),
      handleInputEvent
    );
  }

  // ── Full-page /search augmentation ────────────────────────────
  // Shopify caps `for product in collection.products` at 50 items
  // per render even when a higher `limit:` is passed, so the
  // server-side internal-search adapter in sections/main-search.liquid
  // can only surface products that fall in the first 50 of the
  // `int-idx-xyz` smart collection. With ~520 Parfumi products this
  // means anything beyond position 50 (e.g. AMOUR No. 234 at ~274)
  // is invisible from the /search page.
  //
  // Fix: on /search we run MiniSearch over the FULL client-side
  // index, diff matches against the IDs already rendered on the
  // page, and for each missing match we fetch the canonical card
  // HTML via /products/<handle>?view=search-card. The single-product
  // template just renders {% render 'product-grid-item' %} so the
  // injected card is byte-for-byte identical to what Shopify would
  // have produced if the loop hadn't been capped.
  // Match either the canonical Shopify search grid OR the
  // `.internal-search-grid` fallback that sections/main-search.liquid
  // unhides when search.results_count == 0 but the int-idx-xyz adapter
  // surfaced one or more products. Both flavours are real card grids
  // and the JS injection should append into whichever one exists.
  var FULL_SEARCH_GRID_SEL = '#CollectionAjaxContent .new-grid.product-grid.collection-grid';
  var FULL_SEARCH_INJECTED = 'data-internal-search-injected';
  var FULL_SEARCH_MAX_INJECT = 60; // cap parallel card fetches per query

  function isFullSearchPage() {
    if (typeof document === 'undefined' || !document.body) return false;
    if (document.body.classList && document.body.classList.contains('template-search')) return true;
    var path = (window.location && window.location.pathname) || '';
    return /\/search(\/|$)/i.test(path);
  }

  function getSearchQueryFromUrl() {
    try {
      var sp = new URL(window.location.href).searchParams;
      var q = sp.get('q') || '';
      return q.replace(/\*/g, '').trim();
    } catch (e) {
      return '';
    }
  }

  function getRenderedProductIds() {
    var ids = {};
    // Shopify renders [data-product-id="..."] on each grid item card
    // (snippets/product-grid-item.liquid). Anything matching counts.
    var nodes = document.querySelectorAll('#CollectionAjaxContent [data-product-id]');
    for (var i = 0; i < nodes.length; i++) {
      var v = nodes[i].getAttribute('data-product-id');
      if (v) ids[v] = true;
    }
    return ids;
  }

  function fetchSearchCard(handle) {
    var url = '/products/' + encodeURIComponent(handle) + '?view=search-card';
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('search-card HTTP ' + r.status + ' for ' + handle);
      return r.text();
    });
  }

  function ensureInjectionGrid(mainGrid) {
    var injected = mainGrid.querySelector(':scope > [' + FULL_SEARCH_INJECTED + ']');
    if (injected) return injected;
    injected = document.createElement('div');
    injected.setAttribute(FULL_SEARCH_INJECTED, '1');
    injected.style.display = 'contents';
    mainGrid.appendChild(injected);
    return injected;
  }

  function hydrateInjectedSearchCards(root) {
    if (!root) return;
    // Theme CSS sets `.image-element { opacity: 0 }` until the parent
    // `<image-element data-aos="image-fade-in">` receives `.aos-animate`
    // from AOS. Cards fetched via ?view=search-card are injected after
    // AOS.init (disableMutationObserver:true), so they never animate in
    // and stay invisible even though the <img src> loaded fine.
    if (window.AOS && typeof window.AOS.refreshHard === 'function') {
      try { window.AOS.refreshHard(); } catch (e) {}
    }
    var aosHosts = root.querySelectorAll('image-element[data-aos="image-fade-in"]');
    for (var i = 0; i < aosHosts.length; i++) {
      if (!aosHosts[i].classList.contains('aos-animate')) {
        aosHosts[i].classList.add('aos-animate');
      }
    }
    var imgs = root.querySelectorAll('.image-element');
    for (var j = 0; j < imgs.length; j++) {
      imgs[j].style.opacity = '1';
    }
    if (typeof window.AmourInspiredHydrate === 'function') {
      try { window.AmourInspiredHydrate(root); } catch (e) {}
    }
    if (window.Currency && typeof window.Currency.convertAll === 'function') {
      try { window.Currency.convertAll(); } catch (e) {}
    }
  }

  function countDistinctRenderedProducts() {
    // Count UNIQUE product IDs across the entire CollectionAjaxContent
    // — guards against quick-shop modal duplicating data-product-id on
    // a sibling element and double-counting cards.
    var seen = Object.create(null);
    var nodes = document.querySelectorAll('#CollectionAjaxContent [data-product-id]');
    for (var i = 0; i < nodes.length; i++) {
      var v = nodes[i].getAttribute('data-product-id');
      if (v) seen[v] = true;
    }
    return Object.keys(seen).length;
  }

  function updateResultCount() {
    // Recompute the count from the DOM rather than incrementing the
    // server's number. The server-side count tracks filtered_search_count
    // + internal_matches_count which is bounded by Shopify's 50-item
    // collection iteration cap, so we can't reliably derive the visible
    // count by adding to it. Counting actual cards is always correct.
    var visible = countDistinctRenderedProducts();
    if (!visible) return;
    var visibleStr = String(visible);

    var nodes = document.querySelectorAll('[data-collection-count]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.textContent && /\d+/.test(node.textContent)) {
        node.textContent = node.textContent.replace(/\d+/, visibleStr);
      }
    }
    var bcNodes = document.querySelectorAll('.breadcrumb span, nav.breadcrumb span');
    for (var j = 0; j < bcNodes.length; j++) {
      var bc = bcNodes[j];
      if (bc.textContent && /\(\d+\)/.test(bc.textContent)) {
        bc.textContent = bc.textContent.replace(/\(\d+\)/, '(' + visibleStr + ')');
      }
    }
    if (document.title && /\(\d+\)/.test(document.title)) {
      document.title = document.title.replace(/\(\d+\)/, '(' + visibleStr + ')');
    }
  }

  function findNoResultsHeader(contentRoot) {
    // sections/main-search.liquid renders a fallback section-header with
    // a single <p> "no results" message when total_count is 0. It's
    // always the LAST .section-header in .search-content (the first is
    // the page title h1). Identify it by content + position so we can
    // remove it cleanly when injecting matches client-side.
    var headers = contentRoot.querySelectorAll('.section-header');
    for (var i = headers.length - 1; i >= 0; i--) {
      var h = headers[i];
      var p = h.querySelector('p');
      if (!p) continue;
      var txt = (p.textContent || '').toLowerCase();
      if (
        txt.indexOf('ni prineslo') !== -1 ||
        txt.indexOf('no results') !== -1 ||
        txt.indexOf('ergab keine') !== -1 ||
        txt.indexOf('nije dao') !== -1 ||
        txt.indexOf('nem hozott') !== -1 ||
        txt.indexOf('nessun risultato') !== -1
      ) {
        return h;
      }
    }
    return null;
  }

  function removeNoResultsMessage() {
    var contentRoot = document.querySelector('.search-content .page-width.page-content') || document;
    var h = findNoResultsHeader(contentRoot);
    if (h && h.parentNode) h.parentNode.removeChild(h);
  }

  function buildEmptyGridForNoResultsPage() {
    // When the server renders the no-results state the entire
    // CollectionAjaxContent grid is missing. Build a minimal grid
    // skeleton that matches what main-search.liquid would have
    // produced and place it AFTER the <predictive-search> form, so
    // the injected cards stay below the heading + search input but
    // above the (now-removed) "no results" section-header. Inserting
    // earlier would push the search input below the cards, which
    // looks broken on desktop.
    var contentRoot = document.querySelector('.search-content .page-width.page-content');
    if (!contentRoot) return null;

    // Anchor preference: predictive-search form > no-results header > append.
    var anchorAfter =
      contentRoot.querySelector('predictive-search') ||
      findNoResultsHeader(contentRoot);

    var wrap = document.createElement('div');
    wrap.id = 'CollectionAjaxContent';
    wrap.className = 'grid collection-content';
    // Use one-whole, not four-fifths: when the server renders
    // total_count == 0 it skips CollectionAjaxContent AND the inline
    // CSS from collection-grid-filters that would widen content to
    // 100% after hiding the sidebar. Without that override,
    // four-fifths (80%) stays left-aligned with empty space on the right.
    wrap.innerHTML =
      '<div class="grid__item medium-up--one-whole grid__item--content">' +
        '<div class="new-grid product-grid collection-grid" data-view="medium"></div>' +
      '</div>';
    if (anchorAfter && anchorAfter.parentNode) {
      anchorAfter.parentNode.insertBefore(wrap, anchorAfter.nextSibling);
    } else {
      contentRoot.appendChild(wrap);
    }
    return wrap.querySelector('.new-grid.product-grid.collection-grid');
  }

  function injectFullSearchResults() {
    if (!isFullSearchPage()) return;
    var query = getSearchQueryFromUrl();
    if (!query || query.length < MIN_QUERY_LENGTH) return;

    loadIndex().then(function () {
      var matches = search(query);
      if (!matches.length) return;

      var renderedIds = getRenderedProductIds();
      var missing = [];
      for (var i = 0; i < matches.length; i++) {
        var idStr = String(matches[i].id);
        if (!renderedIds[idStr]) missing.push(matches[i]);
        if (missing.length >= FULL_SEARCH_MAX_INJECT) break;
      }
      if (!missing.length) return;

      var mainGrid = document.querySelector(FULL_SEARCH_GRID_SEL);
      if (!mainGrid) {
        mainGrid = buildEmptyGridForNoResultsPage();
        if (!mainGrid) return;
        removeNoResultsMessage();
      }
      // Defensive: if we landed on the `.internal-search-grid` while it
      // still has the `hidden` attribute (race against the inline
      // unhide script in main-search.liquid), reveal it now so the
      // injected cards plus any pre-rendered ones are visible.
      if (mainGrid.hasAttribute('hidden')) mainGrid.removeAttribute('hidden');

      var injectionRoot = ensureInjectionGrid(mainGrid);

      // Fire all card fetches in parallel and append in MiniSearch
      // ranking order as they complete.
      var promises = missing.map(function (m) {
        return fetchSearchCard(m.handle).then(function (html) {
          return { id: m.id, html: html };
        }).catch(function () { return null; });
      });

      Promise.all(promises).then(function (rows) {
        var addedCount = 0;
        var tmp = document.createElement('div');
        for (var i = 0; i < rows.length; i++) {
          var row = rows[i];
          if (!row || !row.html) continue;
          tmp.innerHTML = row.html;
          var card = tmp.firstElementChild;
          if (!card) continue;
          // Unwrap our [data-search-card-id] wrapper so the injected
          // node sits at the same DOM depth as Shopify's own cards
          // and inherits the .new-grid > * grid item styles.
          while (card.firstChild) {
            injectionRoot.appendChild(card.firstChild);
          }
          addedCount++;
        }
        if (addedCount) {
          updateResultCount();
          hydrateInjectedSearchCards(injectionRoot);
        }
      });
    }).catch(function () { /* index load failure already logged */ });
  }

  function init() {
    bindInputs();
    bindDiscoveryDelegation();
    attachObservers();
    injectFullSearchResults();

    // Watch for predictive-search elements that get injected later
    // (e.g. when the mobile search drawer mounts on first open).
    var bodyObserver = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          var hasMatch =
            (typeof n.matches === 'function' &&
              (n.matches('predictive-search') || n.matches(SEARCH_INPUT_SELECTOR))) ||
            (typeof n.querySelector === 'function' &&
              (n.querySelector('predictive-search') || n.querySelector(SEARCH_INPUT_SELECTOR)));
          if (hasMatch) {
            bindInputs();
            attachObservers();
            return;
          }
        }
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.internalSearch = {
    loadIndex: loadIndex,
    search: search,
    products: function () { return products.slice(); }
  };

  // AMOUR race-fix: warm the index shortly after load (browser idle) so the
  // very first keystroke reranks instantly and the native (unranked) results
  // never flash first. This is a pure prefetch — it does NOT change any
  // rendering or markup, it only loads the index earlier than on focus/input.
  (function warmIndexOnIdle() {
    function warm() { try { loadIndex(); } catch (e) {} }
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(warm, { timeout: 2500 });
    } else {
      setTimeout(warm, 1200);
    }
  })();
})();
